/*  Shared  */
import { CellContext, Row } from '@tanstack/react-table';
import { UserBubbles } from 'application/Shared/Components/Avatars';

/*  React  */
import React from 'react';

import { ColumnFilter } from '../Table/types';

/**
 * Mapping of userCount filter options to number of users.
 */
const userCountMap: Record<string, number> = {
    noUsers: 0,
    oneUser: 1,
    twoUsers: 2,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    threeOrMore: 3
};

/**
 * Configuration object for user count filtering control.
 */
export const userCountFilter: ColumnFilter<unknown> = {
    control: 'Radio',
    defaultOptions: [{ options: Object.keys(userCountMap) }]
};

/**
 * Filters data based on the number of users / length of an array.
 *
 * @template TData - The type of data being filtered
 * @param {Row<TData>} row - The row of data to check
 * @param {string} columnId - The ID of the column containing the users
 * @param {string} filterValue - The filter value (e.g., ['noUsers'], ['twoUsers'])
 *
 * @throws {Error} If the filterValue is not a valid key in userCountMap
 * @returns {boolean} - True if the number of users is equal to the filter, false otherwise
 *
 * @example
 * // Returns true if the number of users in the row is two
 * lastXDays(row, 'userCount', ['twoUsers'])
 *
 */
export function moreThanXUsers<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue[0]) return true;

    if (!(filterValue[0] in userCountMap)) {
        throw new Error(`Invalid filter value: ${filterValue}`);
    }

    const rowNumberOfUsers: number = (row.getValue(columnId) as Array<unknown>)
        ?.length;
    const comparisonNumberOfUsers = userCountMap[filterValue[0]];

    // Set both dates to the start of their respective days

    if (filterValue[0] === 'threeOrMore') {
        return rowNumberOfUsers >= comparisonNumberOfUsers;
    }
    return rowNumberOfUsers === comparisonNumberOfUsers;
}

export const UserBubblesCell = <TData,>({
    cell,
    dataIsUser,
    row,
    ...rest
}: CellContext<TData, string | number | Date | null> & {
    dataIsUser?: boolean;
    userCountAccessorKey?: string;
}) => {
    const totalUserCount =
        (row.original as { userCount?: number }).userCount ?? 0;
    return (
        <UserBubbles
            align="left"
            dataIsUser={dataIsUser}
            // @ts-expect-error I can't figure this one out
            users={row.original[cell.column.columnDef.accessorKey]}
            userCount={totalUserCount}
            max={3}
            {...rest}
        />
    );
};
